import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { getMySalon, saveSalonProfile } from "@/lib/salon.functions";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { SalonImagesCard } from "@/components/SalonImagesCard";
import { SalonPagePreviewCard } from "@/components/SalonPagePreviewCard";
import { AdsPreviewCard } from "@/components/AdsPreviewCard";
import { LandingTemplateCard } from "@/components/LandingTemplateCard";
import { OpeningHoursCard } from "@/components/OpeningHoursCard";

export const Route = createFileRoute("/_authenticated/dashboard/settings")({
  component: SettingsPage,
});

function SettingsPage() {
  const queryClient = useQueryClient();
  const fetchSalon = useServerFn(getMySalon);
  const save = useServerFn(saveSalonProfile);
  const { data, isLoading } = useQuery({ queryKey: ["my-salon"], queryFn: () => fetchSalon() });

  const mutation = useMutation({
    mutationFn: (values: Record<string, string | null | number>) => save({ data: values as never }),
    onSuccess: async () => {
      toast.success("Gespeichert.");
      await queryClient.invalidateQueries({ queryKey: ["my-salon"] });
    },
    onError: () => toast.error("Speichern fehlgeschlagen."),
  });

  if (isLoading || !data?.salon) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }

  const salon = data.salon;

  return (
    <div className="space-y-6">
    <SalonPagePreviewCard slug={salon.slug} />
    <AdsPreviewCard
      salon={{
        name: salon.name,
        slug: salon.slug,
        city: salon.city ?? null,
        phone: salon.phone ?? null,
        booking_url: salon.booking_url ?? null,
      }}
    />
    <Card>
      <CardHeader>
        <CardTitle className="font-display text-xl">Salon-Daten</CardTitle>
        <CardDescription>
          Diese Angaben erscheinen auf deiner Salon-Seite /s/{salon.slug}.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="space-y-5"
          onSubmit={(event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            const value = (key: string) => String(form.get(key) ?? "").trim();
            mutation.mutate({
              name: value("name"),
              phone: value("phone"),
              street: value("street"),
              postal_code: value("postal_code"),
              city: value("city"),
              website_url: value("website_url") || null,
              booking_url: value("booking_url") || null,
              description: value("description") || null,
              instagram_url: value("instagram_url") || null,
              facebook_url: value("facebook_url") || null,
              tiktok_url: value("tiktok_url") || null,
            });
          }}
        >
          {[
            { name: "name", label: "Salonname", value: salon.name, required: true },
            { name: "phone", label: "Telefon", value: salon.phone ?? "", required: true },
            { name: "street", label: "Straße & Nr.", value: salon.street ?? "", required: true },
            { name: "postal_code", label: "PLZ", value: salon.postal_code ?? "", required: true },
            { name: "city", label: "Stadt", value: salon.city ?? "", required: true },
            { name: "website_url", label: "Website", value: salon.website_url ?? "" },
            { name: "booking_url", label: "Buchungslink", value: salon.booking_url ?? "" },
            { name: "instagram_url", label: "Instagram", value: salon.instagram_url ?? "" },
            { name: "facebook_url", label: "Facebook", value: salon.facebook_url ?? "" },
            { name: "tiktok_url", label: "TikTok", value: salon.tiktok_url ?? "" },
          ].map((field) => (
            <div key={field.name} className="space-y-2">
              <Label htmlFor={field.name}>{field.label}</Label>
              <Input
                id={field.name}
                name={field.name}
                defaultValue={field.value}
                required={field.required}
                maxLength={300}
              />
            </div>
          ))}
          <div className="space-y-2">
            <Label htmlFor="description">Kurzbeschreibung</Label>
            <Textarea
              id="description"
              name="description"
              maxLength={600}
              defaultValue={salon.description ?? ""}
            />
          </div>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
            Speichern
          </Button>
        </form>
      </CardContent>
    </Card>
    <OpeningHoursCard hours={data.hours ?? []} />
    <LandingTemplateCard slug={salon.slug} current={salon.landing_template} />
    <SalonImagesCard
      salonId={salon.id}
      websiteUrl={salon.website_url ?? null}
      images={data.images ?? []}
    />
    </div>
  );
}
